import { v4 as uuidv4 } from 'uuid';

import type { ImportedGroupMeta } from '../../utils/csv-grouping/csv-group-meta';
import type { TableNode } from '..';
import { isGroupRow } from './group-row-guards';

/**
 * Project Bobcat — automatic Supervisor / Knowledge Agent node grouping.
 *
 * A solution exported from the platform (`templates/bot.csv`) carries no
 * grouping at all: the five `Group *` CSV columns are a Pro-Studio-only
 * round-trip, so an imported bot arrives ungrouped even when its structure is
 * plainly a Supervisor + Knowledge Agent pipeline. This planner recognises that
 * shape from the node NAMES and proposes the two groups, so the table (and
 * Agent View) show the roles without anyone grouping by hand.
 *
 * Pure and dependency-light — no MobX, no store — so every branch is testable
 * without instantiating the store. Same discipline as `smart-node-numbering.ts`.
 *
 * ## Two naming conventions, detected per solution
 *
 * There is no field that says "this node is the Supervisor" — the role lives in
 * the node NAME, and the platform has written that name two different ways.
 * Both are supported, and which one a solution uses is detected rather than
 * configured (`detectAgentNameDialect`):
 *
 * | Dialect | Looks like | Rule |
 * |---|---|---|
 * | `legacy` | `Supervisor Agent Step 4: SysSupervisorGetContext` | the name STARTS WITH the agent's full name |
 * | `ai-native` | `NLX - Supervisor - get context (SysSupervisorGetContext)` | one ` - `-delimited SEGMENT of the name is the agent's role |
 *
 * The AI-native form is what the current *AI-Native Solution* starter CSV emits
 * (`Edan.AINativeSolution`), where every AI node is named
 * `<solution> - <role> - <step>`. The two rules are kept separate rather than
 * merged into one looser matcher, because each is safe only in its own dialect:
 *
 * - **Prefix, not `includes`, for legacy.** The legacy sample has
 *   `Display Knowledge Agent response (standard)` — a display node that merely
 *   mentions the Knowledge Agent and belongs to neither group. `includes` would
 *   swallow it and, worse, make the two groups overlap.
 * - **A whole segment, not a substring, for AI-native.** The role has to BE one
 *   of the `-`-delimited parts. That is what keeps
 *   `NLX - error or malformed response` — a shared error node sitting right
 *   after the Knowledge block — out of both groups, while still matching
 *   `NLX - [OPTIONAL] - Supervisor - redact any PII in the transcript`, where
 *   the role is not in a fixed position.
 *
 * Detection requires BOTH agents under the SAME dialect — with enough rows each
 * to be worth grouping — and legacy wins a tie, so a solution that already
 * grouped correctly keeps grouping identically.
 *
 * **The group names do not change with the dialect.** An AI-native bot whose
 * nodes say `NLX - Supervisor - …` still gets a group called
 * `Supervisor Agent`, because the group name is the ROLE being shown, and
 * because `findAutoGroupRows` / `resetAutoAgentGroups` identify this feature's
 * own groups by that name.
 *
 * ## Why a contiguous span, and why filler is absorbed
 *
 * Pro Studio groups must be CONTIGUOUS (`assertGroupInvariants`,
 * `group-row-ordering.ts`, and the `members-stay-contiguous` e2e). Real bots
 * interleave: the sample has `SysAssignVariable` (70010) sitting between two
 * Supervisor steps. So a plain "all matching rows" group would be illegal.
 *
 * Instead each agent gets the run holding the MOST of its own matches (not the
 * widest one — see `longestRun`) that
 *   - starts and ends on one of its own matches,
 *   - may absorb non-matching rows in between (that `SysAssignVariable`), and
 *   - never crosses a match belonging to the OTHER agent.
 *
 * That last clause is what stops the Supervisor group swallowing the whole bot:
 * its matches resume at 7900/7910 AFTER the Knowledge Agent block, so a naive
 * first-to-last span would cover everything between. The run stops at the first
 * Knowledge Agent node and the trailing pair is simply left ungrouped.
 *
 * ## Exactly TWO groups — this planner never touches the leftovers
 *
 * Ungrouped rows (of any node type) are never planned into a group by this
 * feature — they just stay ungrouped, reported by canvas's "Ungrouped: N"
 * indicator (`canvas-view-model.ts`'s `buildUngroupedSummary`). An earlier
 * version wrote a real "Use-case Agents" group from leftover ACTION rows —
 * wrong twice over (it reorganised the solution, and it lumped Decision
 * nodes in with actions) — see `AUTO_GROUP_NAMES`'s note below and
 * `STALE_AUTO_GROUP_NAMES`.
 */

/**
 * The two agents, in the order that fixes which colour each takes.
 *
 * `groupName` is what the created group is CALLED, in both dialects, and is the
 * only marker identifying a group this feature made. `legacyPrefix` and
 * `segments` are how a node is recognised as belonging to the agent — see the
 * dialect table above.
 */
export const AUTO_AGENT_GROUPS = [
  {
    groupName: 'Supervisor Agent',
    groupColor: '#8573E7',
    legacyPrefix: 'Supervisor Agent',
    segments: ['supervisor', 'supervisor agent'],
  },
  {
    groupName: 'Knowledge Agent',
    groupColor: '#34785A',
    legacyPrefix: 'Knowledge Agent',
    segments: ['knowledge', 'knowledge agent'],
  },
] as const;

type AgentDef = (typeof AUTO_AGENT_GROUPS)[number];

/** Which naming convention a solution's nodes use. */
export type AgentNameDialect = 'legacy' | 'ai-native';

/** A run shorter than this is noise, not a pipeline. */
export const MIN_AUTO_GROUP_MEMBERS = 2;

/**
 * What separates the parts of an AI-native node name.
 *
 * A hyphen with whitespace on BOTH sides, so `chit-chat` in
 * `NLX - Supervisor - chit-chat` stays one word rather than splitting into two
 * segments. En and em dashes are accepted because names are typed by hand and
 * an editor that auto-corrects `-` to `–` should not silently stop the
 * detection.
 */
const NAME_SEGMENT_SEPARATOR = /\s+[-–—]\s+/;

const trimmedName = (row: TableNode): string | null => {
  const name = row.nodeName;
  if (typeof name !== 'string') return null;
  const trimmed = name.trim();
  return trimmed.length ? trimmed : null;
};

/** `Supervisor Agent Step 4: …` — the name starts with the agent's full name. */
const matchesLegacyName = (row: TableNode, def: AgentDef): boolean => {
  const name = trimmedName(row);
  return (
    !!name && name.toLowerCase().startsWith(def.legacyPrefix.toLowerCase())
  );
};

/**
 * `NLX - Supervisor - get context (…)` — one whole segment IS the agent's role.
 *
 * A name with no separator cannot match: this dialect is the `<x> - <role> -
 * <step>` convention, and treating a bare `Supervisor…` as a hit here would
 * just be the legacy prefix rule wearing a different name — including for the
 * `Display Knowledge Agent response` node that rule exists to exclude.
 */
const matchesAiNativeName = (row: TableNode, def: AgentDef): boolean => {
  const name = trimmedName(row);
  if (!name) return false;

  const segments = name.split(NAME_SEGMENT_SEPARATOR);
  if (segments.length < 2) return false;

  return segments.some((segment) =>
    (def.segments as readonly string[]).includes(segment.trim().toLowerCase())
  );
};

const MATCHERS: Record<
  AgentNameDialect,
  (row: TableNode, def: AgentDef) => boolean
> = {
  legacy: matchesLegacyName,
  'ai-native': matchesAiNativeName,
};

/**
 * Rows that may be grouped: not a group header, and not already in a group.
 *
 * Shared by detection and planning so the two can never disagree about which
 * rows they are looking at.
 */
const eligibilityOf = (rows: TableNode[]): boolean[] =>
  rows.map((row) => !isGroupRow(row) && !row.settings?.groupId);

/**
 * Which naming convention this solution uses, or `null` when neither
 * recognises BOTH agents.
 *
 * A dialect counts as recognised only when it finds at least
 * `MIN_AUTO_GROUP_MEMBERS` rows for EACH agent — the same floor a group has to
 * clear to be worth creating. One match is never enough to group on (a run of
 * one match is a span of one row, which `planAutoAgentGroups` rejects), so
 * without the floor a single stray `Knowledge Agent …` node in an otherwise
 * AI-native bot would win the dialect for `legacy` and silently take the whole
 * solution's grouping down with it.
 *
 * Legacy is tried first so that a solution which grouped correctly before this
 * function existed groups identically now. A bot naming its nodes both ways is
 * not a case worth planning for — it gets the legacy reading, and the
 * AI-native nodes are simply not matched, which is visible and inert rather
 * than an invented third behaviour.
 *
 * `eligible` is the row-eligibility mask from `eligibilityOf`; pass the one you
 * already have so detection and planning cannot disagree (and so the mask is
 * not built twice).
 */
export const detectAgentNameDialect = (
  rows: TableNode[],
  eligible: boolean[] = eligibilityOf(rows)
): AgentNameDialect | null => {
  const recognisesBothAgents = (dialect: AgentNameDialect) =>
    AUTO_AGENT_GROUPS.every((def) => {
      let matched = 0;
      for (let i = 0; i < rows.length; i += 1) {
        if (eligible[i] && MATCHERS[dialect](rows[i], def)) {
          matched += 1;
          if (matched >= MIN_AUTO_GROUP_MEMBERS) return true;
        }
      }
      return false;
    });

  if (recognisesBothAgents('legacy')) return 'legacy';
  if (recognisesBothAgents('ai-native')) return 'ai-native';
  return null;
};

/**
 * True when the table already carries ANY grouping — a group header row, or a
 * row pointing at one. The auto-grouping never runs over a solution someone has
 * already organised.
 */
export const hasAnyGrouping = (rows: TableNode[]): boolean =>
  rows.some((row) => isGroupRow(row) || !!row.settings?.groupId);

/**
 * True when a group named after either agent already exists. Checked
 * independently of `hasAnyGrouping` so a re-load of an auto-grouped solution is
 * a no-op rather than a second set of groups.
 */
export const hasAgentGroups = (rows: TableNode[]): boolean => {
  const names = new Set(
    AUTO_AGENT_GROUPS.map((g) => g.groupName.toLowerCase())
  );
  return rows.some(
    (row) =>
      isGroupRow(row) &&
      typeof row.settings?.groupName === 'string' &&
      names.has(row.settings.groupName.trim().toLowerCase())
  );
};

/** Every group name this feature creates. */
export const AUTO_GROUP_NAMES: string[] = AUTO_AGENT_GROUPS.map(
  (g) => g.groupName
);

/**
 * Group names this feature USED to create and no longer does.
 *
 * An earlier version wrote a real "Use-case Agents" group from the leftover
 * rows. That was wrong twice over — it reorganised the solution, and it lumped
 * Decision nodes in with actions — so leftover rows are no longer grouped at
 * all; they just stay ungrouped (see the module doc comment above).
 *
 * Solutions grouped by that version still carry the group in Hasura, where it
 * renders as a real section full of Decision nodes. Listing it here lets the
 * loader clean up after itself instead of leaving someone to ungroup it by
 * hand.
 */
export const STALE_AUTO_GROUP_NAMES: string[] = ['Use-case Agents'];

/** Group rows left behind by a previous version of this feature. */
export const findStaleAutoGroupRows = (rows: TableNode[]): TableNode[] => {
  const names = new Set(STALE_AUTO_GROUP_NAMES.map((n) => n.toLowerCase()));
  return rows.filter(
    (row) =>
      isGroupRow(row) &&
      typeof row.settings?.groupName === 'string' &&
      names.has(row.settings.groupName.trim().toLowerCase())
  );
};

/**
 * The group header rows this feature created, identified by name.
 *
 * Name is the only marker available: `settings` has no field for "who made
 * this", and adding one would put a Bobcat-only key in the JSON bag that ships
 * to every solution. The consequence is honest but worth knowing — a group a
 * user named "Supervisor Agent" by hand is indistinguishable from an
 * auto-created one, so a reset would remove theirs too.
 */
export const findAutoGroupRows = (rows: TableNode[]): TableNode[] => {
  const names = new Set(AUTO_GROUP_NAMES.map((n) => n.toLowerCase()));
  return rows.filter(
    (row) =>
      isGroupRow(row) &&
      typeof row.settings?.groupName === 'string' &&
      names.has(row.settings.groupName.trim().toLowerCase())
  );
};

/**
 * The run of `mine` containing the MOST of its own matches, expressed as the
 * index span [start, end] it covers: it begins and ends on one of `mine`,
 * absorbs non-matching rows in between, and never crosses an index in `theirs`.
 * Returns null when `mine` is empty.
 *
 * "Most matches", NOT the widest span. Ranking by `end - start` picks whichever
 * run happens to straddle the most rows, which is the opposite of what this is
 * for: a run of two matches 20 rows apart would beat a dense cluster of three
 * adjacent ones and drag 19 unrelated nodes into the group with it. The filler a
 * run absorbs is a cost it pays, not evidence that it is the agent's block.
 */
const longestRun = (
  mine: number[],
  theirs: Set<number>
): { start: number; end: number } | null => {
  if (!mine.length) return null;

  // Cut `mine` into runs first, then pick one. Accumulating a "best so far"
  // inside a closure is the obvious shape but defeats TypeScript's control-flow
  // narrowing (it cannot see the assignment, so the variable reads as `null`
  // afterwards); splitting the two steps keeps the types honest.
  const runs: { start: number; end: number; matches: number }[] = [
    { start: mine[0], end: mine[0], matches: 1 },
  ];

  for (let i = 1; i < mine.length; i += 1) {
    const idx = mine[i];
    const run = runs[runs.length - 1];

    // Does the other agent sit anywhere between the current run and this match?
    let blocked = false;
    for (let between = run.end + 1; between < idx; between += 1) {
      if (theirs.has(between)) {
        blocked = true;
        break;
      }
    }

    if (blocked) {
      runs.push({ start: idx, end: idx, matches: 1 });
    } else {
      run.end = idx;
      run.matches += 1;
    }
  }

  // First run wins a tie, so an equally dense earlier block is preferred — the
  // pipeline reads top-to-bottom.
  const best = runs.reduce((a, b) => (b.matches > a.matches ? b : a));
  return { start: best.start, end: best.end };
};

/**
 * Propose the two agent groups for `rows` (already in display order).
 *
 * Returns `[]` — meaning "do nothing" — unless BOTH agents are recognised under
 * ONE naming dialect, as required: a bot with only one of them is not the
 * Supervisor+Knowledge shape this feature claims to detect.
 *
 * The dialect is decided once, for the whole solution, and then used for every
 * row. Matching each row under whichever rule happens to fit it would let a
 * single bot be read two ways at once, which is how a "Knowledge" segment in an
 * otherwise legacy bot would pull an unrelated node into a group.
 */
export const planAutoAgentGroups = (rows: TableNode[]): ImportedGroupMeta[] => {
  // Group headers are not candidates, and neither is anything already grouped.
  const eligible = eligibilityOf(rows);

  const dialect = detectAgentNameDialect(rows, eligible);
  if (!dialect) return [];

  const indicesFor = (def: AgentDef) =>
    rows.reduce<number[]>((acc, row, i) => {
      if (eligible[i] && MATCHERS[dialect](row, def)) acc.push(i);
      return acc;
    }, []);

  const matches = AUTO_AGENT_GROUPS.map((def) => ({
    def,
    indices: indicesFor(def),
  }));

  // Both agents must be present. `detectAgentNameDialect` has already
  // established this; kept so the invariant is enforced where it is relied on.
  if (matches.some((m) => !m.indices.length)) return [];

  const plans: ImportedGroupMeta[] = [];
  const claimed = new Set<number>();

  for (const { def, indices } of matches) {
    const theirs = new Set(
      matches.filter((m) => m.def !== def).flatMap((m) => m.indices)
    );

    const run = longestRun(indices, theirs);
    if (!run) return [];

    const members: ImportedGroupMeta['members'] = [];
    for (let i = run.start; i <= run.end; i += 1) {
      // A row already claimed by the other agent's run, or ineligible, would
      // break contiguity — bail rather than emit an invalid group.
      if (claimed.has(i) || !eligible[i]) return [];

      const nodeNumber = Number(rows[i].nodeNumber);
      if (!Number.isFinite(nodeNumber) || nodeNumber <= 0) return [];

      members.push({ nodeNumber, order: members.length + 1 });
    }

    if (members.length < MIN_AUTO_GROUP_MEMBERS) return [];

    plans.push({
      groupId: uuidv4(),
      groupName: def.groupName,
      groupColor: def.groupColor,
      // Collapsed by default: the point is to make a long bot readable at a
      // glance, which an expanded group does not do.
      collapsed: true,
      members,
    });

    for (let i = run.start; i <= run.end; i += 1) claimed.add(i);
  }

  return plans;
};
